import { BaseSupabaseService } from './BaseSupabaseService';
import {
  CheckoutLead,
  CheckoutLeadOnboardingStatus,
  CheckoutLeadOnboardingStatusRequestItem,
  CommercialLeadsPollResult,
  CommercialLeadListing,
  CommercialLeadFilters,
  OnboardingLeadFilters,
  Lead,
  PaginatedResponse,
  mapStudentClassificationFromJson,
  type LeadPersonaType,
  type LeadTemperatureType,
  type StudentClassification,
} from '../types';
import { handleServiceError } from '../utils/errorHandler';
import { mapParqYesAnswersFromJson } from '../utils/parqYesAnswers';
import { mapProjectIdentity, normalizeHexColor } from '../utils/project';
import { readIsRecentlyReactivated } from '../utils/studentReactivation';
import {
  readIsPregnant,
  readIsRedFlag,
  readIsSensitive,
  readPregnantUntil,
  readSensitiveReason,
} from '../utils/studentSensitive';
import supabase from '../config/supabase';
import config from '../config/environment';

interface CheckoutLeadResponseRow {
  checkout_id: number;
  student_id?: number | null;
  studentId?: number | null;
  qualification_id?: number | null;
  qualificationId?: number | null;
  student_name: string;
  student_email: string | null;
  student_phone: string | null;
  student_phone_country_code: string | null;
  joined_at: string | null;
  origin: string;
  selected_payment_method: CheckoutLead['selected_payment_method'];
  created_at: string;
  student_subscription_id: number | null;
  plan_id: number | null;
  plan_name: string | null;
  plan_description: string | null;
  price_cents: number | null;
  billing_cycle: string | null;
  loyalty_months: number | null;
  auto_renew: boolean | null;
  is_single_payment: boolean | null;
  benefits: string | null;
  has_photos: boolean;
  has_parq: boolean;
  has_anamnesis: boolean;
  has_paid: boolean;
  paid_at?: string | null;
  active_subscription_paid_at?: string | null;
  activeSubscriptionPaidAt?: string | null;
  parq_has_yes_answers?: boolean;
  parq_yes_answers?: unknown;
  classification: StudentClassification | string | null;
  investment_interest: string | null;
  start_time_expectation: string | null;
  main_goal: string | null;
  training_relationship?: string | null;
  training_experience?: string | null;
  busy_routine_behavior?: string | null;
  weekly_commitment_days?: string | null;
  training_format?: string | null;
  consulting_expectation?: string | null;
  injury_limitation?: string | null;
  cancellation_reason?: string | null;
  commercial_stage?: string | null;
  commercial_owner_portal_user_id?: number | null;
  commercialOwnerPortalUserId?: number | null;
  commercial_owner_name?: string | null;
  commercialOwnerName?: string | null;
  lead_classification_type?: string | null;
  lead_classification_text?: string | null;
  lead_classification_text_color?: string | null;
  lead_classification_bg_color?: string | null;
  lead_classification_opacity?: number | string | null;
  persona_type?: string | null;
  persona_text?: string | null;
  persona_text_color?: string | null;
  persona_bg_color?: string | null;
  existing_student_match?: {
    student_id?: number | null;
    studentId?: number | null;
    full_name?: string | null;
    fullName?: string | null;
    email?: string | null;
    phone_country_code?: string | null;
    phoneCountryCode?: string | null;
    phone_number?: string | null;
    phoneNumber?: string | null;
    is_inactive?: boolean | null;
    isInactive?: boolean | null;
    matched_by_email?: boolean | null;
    matchedByEmail?: boolean | null;
    matched_by_phone?: boolean | null;
    matchedByPhone?: boolean | null;
  } | null;
  existingStudentMatch?: CheckoutLead['existingStudentMatch'];
  clint_sync_error?: string | null;
  clintSyncError?: string | null;
  project_code?: string | null;
  projectCode?: string | null;
  project_name?: string | null;
  projectName?: string | null;
  project_text_color?: string | null;
  projectTextColor?: string | null;
  project_bg_color?: string | null;
  projectBgColor?: string | null;
  is_reactivated?: boolean | null;
  isReactivated?: boolean | null;
  is_sensitive?: boolean | null;
  isSensitive?: boolean | null;
  sensitive_reason?: string | null;
  sensitiveReason?: string | null;
  is_red_flag?: boolean | null;
  isRedFlag?: boolean | null;
  can_start_new_checkout?: boolean | null;
  canStartNewCheckout?: boolean | null;
}

function hexToRgba(hex: string, alpha: number): string {
  const normalized = hex.replace('#', '').toUpperCase();
  const r = Number.parseInt(normalized.slice(0, 2), 16);
  const g = Number.parseInt(normalized.slice(2, 4), 16);
  const b = Number.parseInt(normalized.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function normalizeOpacity(value: unknown): number {
  const parsed = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(parsed)) {
    return 1;
  }

  return Math.min(1, Math.max(0, parsed));
}

function readResponseString(
  row: object,
  snakeKey: string,
  camelKey: string,
): string {
  const record = row as Record<string, unknown>;
  const snakeValue = record[snakeKey];
  if (typeof snakeValue === 'string' && snakeValue.trim()) {
    return snakeValue.trim();
  }

  const camelValue = record[camelKey];
  if (typeof camelValue === 'string' && camelValue.trim()) {
    return camelValue.trim();
  }

  return '';
}

function readNullablePositiveInt(
  row: object,
  snakeKey: string,
  camelKey: string,
): number | null {
  const record = row as Record<string, unknown>;
  const raw = record[snakeKey] ?? record[camelKey];
  const parsed = Number(raw);
  if (!Number.isInteger(parsed) || parsed <= 0) {
    return null;
  }

  return parsed;
}

const LEAD_TEMPERATURE_TYPES: readonly LeadTemperatureType[] = [
  'super_hot',
  'hot',
  'warm',
  'cold',
];

const LEAD_PERSONA_TYPES: readonly LeadPersonaType[] = [
  'super_persona',
  'persona',
  'non_persona',
];

export function mapLeadTemperatureFromResponse(row: object): CheckoutLead['leadTemperature'] {
  const type = readResponseString(row, 'lead_classification_type', 'leadClassificationType');
  const label = readResponseString(row, 'lead_classification_text', 'leadClassificationText');
  const backgroundHex = normalizeHexColor(
    readResponseString(row, 'lead_classification_bg_color', 'leadClassificationBgColor') || null,
  );
  const text = normalizeHexColor(
    readResponseString(row, 'lead_classification_text_color', 'leadClassificationTextColor') || null,
  );
  const opacity = normalizeOpacity(
    (row as Record<string, unknown>).lead_classification_opacity
    ?? (row as Record<string, unknown>).leadClassificationOpacity,
  );

  if (
    !LEAD_TEMPERATURE_TYPES.includes(type as LeadTemperatureType)
    || !label
    || !backgroundHex
    || !text
  ) {
    return null;
  }

  return {
    type: type as LeadTemperatureType,
    label,
    colors: {
      background: hexToRgba(backgroundHex, opacity),
      text,
      opacity,
    },
  };
}

export function mapLeadPersonaFromResponse(row: object): CheckoutLead['persona'] {
  const type = readResponseString(row, 'persona_type', 'personaType');
  const label = readResponseString(row, 'persona_text', 'personaText');
  const background = normalizeHexColor(
    readResponseString(row, 'persona_bg_color', 'personaBgColor') || null,
  );
  const text = normalizeHexColor(
    readResponseString(row, 'persona_text_color', 'personaTextColor') || null,
  );

  if (
    !LEAD_PERSONA_TYPES.includes(type as LeadPersonaType)
    || !label
    || !background
    || !text
  ) {
    return null;
  }

  return {
    type: type as LeadPersonaType,
    label,
    colors: {
      background,
      text,
    },
  };
}

export function mapExistingStudentMatchFromResponse(
  row: CheckoutLeadResponseRow,
): CheckoutLead['existingStudentMatch'] {
  const raw = row.existing_student_match ?? row.existingStudentMatch;
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const match = raw as Record<string, unknown>;
  const studentId = readNullablePositiveInt(match, 'student_id', 'studentId');
  if (!studentId) {
    return null;
  }

  const fullName = readResponseString(match, 'full_name', 'fullName');
  const emailRaw = match.email;
  const email = typeof emailRaw === 'string' && emailRaw.trim() ? emailRaw.trim() : null;
  const phoneCountryCode = readResponseString(match, 'phone_country_code', 'phoneCountryCode') || null;
  const phoneNumber = readResponseString(match, 'phone_number', 'phoneNumber') || null;
  const isInactive = Boolean(match.is_inactive ?? match.isInactive);
  const matchedByEmail = Boolean(match.matched_by_email ?? match.matchedByEmail);
  const matchedByPhone = Boolean(match.matched_by_phone ?? match.matchedByPhone);

  return {
    studentId,
    fullName,
    email,
    phoneCountryCode,
    phoneNumber,
    isInactive,
    matchedByEmail,
    matchedByPhone,
  };
}

export function mapLeadProjectFromResponse(row: object): CheckoutLead['project'] {
  return mapProjectIdentity(row);
}

export function normalizeCheckoutLead(row: CheckoutLeadResponseRow): CheckoutLead {
  const hasQualification = Boolean(
    row.investment_interest
    || row.start_time_expectation
    || row.main_goal
    || row.training_relationship
    || row.training_experience
    || row.busy_routine_behavior
    || row.weekly_commitment_days
    || row.training_format
    || row.consulting_expectation
    || row.injury_limitation,
  );

  return {
    checkout_id: row.checkout_id,
    student_id: readNullablePositiveInt(row, 'student_id', 'studentId'),
    qualification_id: readNullablePositiveInt(row, 'qualification_id', 'qualificationId'),
    student_name: row.student_name,
    student_email: row.student_email ?? null,
    student_phone: row.student_phone ?? null,
    student_phone_country_code: row.student_phone_country_code ?? null,
    joined_at: row.joined_at ?? null,
    origin: row.origin,
    selected_payment_method: row.selected_payment_method,
    created_at: row.created_at,
    student_subscription_id: row.student_subscription_id,
    has_photos: Boolean(row.has_photos),
    has_parq: Boolean(row.has_parq),
    has_anamnesis: Boolean(row.has_anamnesis),
    has_paid: Boolean(row.has_paid),
    paid_at: row.paid_at ?? null,
    active_subscription_paid_at:
      row.active_subscription_paid_at
      ?? row.activeSubscriptionPaidAt
      ?? null,
    parq_has_yes_answers: mapParqYesAnswersFromJson(row.parq_yes_answers).has_yes_answers
      || Boolean(row.parq_has_yes_answers),
    parq_yes_answers: mapParqYesAnswersFromJson(row.parq_yes_answers ?? {
      has_yes_answers: row.parq_has_yes_answers,
      answers: [],
    }),
    classification: mapStudentClassificationFromJson(row.classification),
    leadTemperature: mapLeadTemperatureFromResponse(row),
    persona: mapLeadPersonaFromResponse(row),
    existingStudentMatch: mapExistingStudentMatchFromResponse(row),
    project: mapLeadProjectFromResponse(row),
    is_reactivated: readIsRecentlyReactivated(row),
    is_sensitive: readIsSensitive(row),
    sensitive_reason: readSensitiveReason(row),
    is_pregnant: readIsPregnant(row),
    pregnant_until: readPregnantUntil(row),
    is_red_flag: readIsRedFlag(row),
    can_start_new_checkout: Boolean(row.can_start_new_checkout ?? row.canStartNewCheckout),
    clintSyncError: (() => {
      const raw = row.clint_sync_error ?? row.clintSyncError;
      return typeof raw === 'string' && raw.trim() ? raw.trim() : null;
    })(),
    plan: {
      id: row.plan_id,
      name: row.plan_name,
      description: row.plan_description,
      price_cents: row.price_cents,
      billing_cycle: row.billing_cycle,
      loyalty_months: row.loyalty_months,
      auto_renew: row.auto_renew,
      is_single_payment: row.is_single_payment,
      benefits: row.benefits,
      project: mapProjectIdentity((row as { plan_project?: unknown }).plan_project),
    },
    qualification: hasQualification || row.cancellation_reason
      ? {
        investment_interest: row.investment_interest ?? null,
        start_time_expectation: row.start_time_expectation ?? null,
        main_goal: row.main_goal ?? null,
        training_relationship: row.training_relationship ?? null,
        training_experience: row.training_experience ?? null,
        busy_routine_behavior: row.busy_routine_behavior ?? null,
        weekly_commitment_days: row.weekly_commitment_days ?? null,
        training_format: row.training_format ?? null,
        consulting_expectation: row.consulting_expectation ?? null,
        injury_limitation: row.injury_limitation ?? null,
        cancellation_reason: row.cancellation_reason ?? null,
      }
      : null,
    cancellation_reason: row.cancellation_reason ?? null,
    commercial_stage: row.commercial_stage ?? null,
    commercial_owner_portal_user_id: readNullablePositiveInt(
      row,
      'commercial_owner_portal_user_id',
      'commercialOwnerPortalUserId',
    ),
    commercial_owner_name: readResponseString(
      row,
      'commercial_owner_name',
      'commercialOwnerName',
    ) || null,
  };
}

class LeadServiceClass extends BaseSupabaseService<Lead> {
  constructor() {
    super('leads');
  }

  async listPaginated(
    page: number = 1,
    pageSize: number = 10,
    filters: CommercialLeadFilters = {},
  ): Promise<PaginatedResponse<CheckoutLead>> {
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData?.session?.access_token;

    const response = await fetch(`${config.supabaseFunctionsUrl}/students`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        apikey: config.supabaseAnonKey,
      },
      body: JSON.stringify({
        action: 'list-leads',
        payload: {
          page,
          pageSize,
          searchName: filters.searchName || undefined,
          classifications: filters.classifications?.length ? filters.classifications : undefined,
          leadTemperatureTypes: filters.leadTemperatureTypes?.length
            ? filters.leadTemperatureTypes
            : undefined,
          personaTypes: filters.personaTypes?.length ? filters.personaTypes : undefined,
          hasPhotos: filters.hasPhotos,
          hasParq: filters.hasParq,
          hasAnamnesis: filters.hasAnamnesis,
          hasPaid: filters.hasPaid,
          paymentMethods: filters.paymentMethods?.length ? filters.paymentMethods : undefined,
          cancellationReasons: filters.cancellationReasons?.length ? filters.cancellationReasons : undefined,
          investmentInterests: filters.investmentInterests?.length ? filters.investmentInterests : undefined,
          startTimeExpectations: filters.startTimeExpectations?.length ? filters.startTimeExpectations : undefined,
          mainGoals: filters.mainGoals?.length ? filters.mainGoals : undefined,
          commercialStages: filters.commercialStages?.length ? filters.commercialStages : undefined,
          joinedAtStart: filters.joinedAtStart || undefined,
          joinedAtEnd: filters.joinedAtEnd || undefined,
          projectCodes: filters.projectCodes?.length ? filters.projectCodes : undefined,
          commercialOwnerIds: filters.commercialOwnerIds?.length ? filters.commercialOwnerIds : undefined,
          isInactive: filters.isInactive === undefined ? false : filters.isInactive,
          ...(filters.declinedRescue === true ? { declinedRescue: true } : {}),
        },
      }),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      handleServiceError(new Error(err?.error?.message || err?.error || 'Erro ao listar leads'), 'listar leads');
    }

    const body = await response.json() as {
      success?: boolean;
      data?: PaginatedResponse<CheckoutLeadResponseRow>;
      error?: { message?: string };
    };

    if (body.success === false || !body.data) {
      handleServiceError(new Error(body.error?.message || 'Erro ao listar leads'), 'listar leads');
    }

    const responseData = body.data;

    return {
      pagination: responseData.pagination,
      data: responseData.data.map(normalizeCheckoutLead),
    };
  }

  async listOnboardingPaginated(
    page: number = 1,
    pageSize: number = 10,
    filters: OnboardingLeadFilters = {},
  ): Promise<PaginatedResponse<CheckoutLead>> {
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData?.session?.access_token;

    const response = await fetch(`${config.supabaseFunctionsUrl}/students`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        apikey: config.supabaseAnonKey,
      },
      body: JSON.stringify({
        action: 'list-onboarding',
        payload: {
          page,
          pageSize,
          searchName: filters.searchName || undefined,
          classifications: filters.classifications?.length ? filters.classifications : undefined,
          hasPhotos: filters.hasPhotos,
          hasParq: filters.hasParq,
          hasAnamnesis: filters.hasAnamnesis,
          joinedAtStart: filters.joinedAtStart || undefined,
          joinedAtEnd: filters.joinedAtEnd || undefined,
          projectCodes: filters.projectCodes?.length ? filters.projectCodes : undefined,
        },
      }),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      handleServiceError(
        new Error(err?.error?.message || err?.error || 'Erro ao listar onboarding'),
        'listar onboarding',
      );
    }

    const body = await response.json() as {
      success?: boolean;
      data?: PaginatedResponse<CheckoutLeadResponseRow>;
      error?: { message?: string };
    };

    if (body.success === false || !body.data) {
      handleServiceError(
        new Error(body.error?.message || 'Erro ao listar onboarding'),
        'listar onboarding',
      );
    }

    const responseData = body.data;

    return {
      pagination: responseData.pagination,
      data: responseData.data.map(normalizeCheckoutLead),
    };
  }

  async pollCommercialLeadsStatus(
    leads: CheckoutLeadOnboardingStatusRequestItem[],
    checkoutLeadsCount: number,
    filters: CommercialLeadFilters = {},
    listing: CommercialLeadListing = 'commercial',
  ): Promise<CommercialLeadsPollResult> {
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData?.session?.access_token;

    const response = await fetch(`${config.supabaseFunctionsUrl}/students`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        apikey: config.supabaseAnonKey,
      },
      body: JSON.stringify({
        action: 'get-leads-onboarding-status',
        payload: {
          leads: leads
            .filter((lead) => Number.isInteger(lead.studentId) && lead.studentId > 0)
            .map((lead) => ({
              studentId: lead.studentId,
              studentSubscriptionId: lead.studentSubscriptionId,
              qualificationId: lead.qualificationId ?? null,
            })),
          checkoutLeadsCount,
          listing,
          searchName: filters.searchName || undefined,
          classifications: filters.classifications?.length ? filters.classifications : undefined,
          leadTemperatureTypes: filters.leadTemperatureTypes?.length
            ? filters.leadTemperatureTypes
            : undefined,
          personaTypes: filters.personaTypes?.length ? filters.personaTypes : undefined,
          hasPhotos: filters.hasPhotos,
          hasParq: filters.hasParq,
          hasAnamnesis: filters.hasAnamnesis,
          hasPaid: filters.hasPaid,
          paymentMethods: filters.paymentMethods?.length ? filters.paymentMethods : undefined,
          cancellationReasons: filters.cancellationReasons?.length ? filters.cancellationReasons : undefined,
          investmentInterests: filters.investmentInterests?.length ? filters.investmentInterests : undefined,
          startTimeExpectations: filters.startTimeExpectations?.length ? filters.startTimeExpectations : undefined,
          mainGoals: filters.mainGoals?.length ? filters.mainGoals : undefined,
          commercialStages: filters.commercialStages?.length ? filters.commercialStages : undefined,
          joinedAtStart: filters.joinedAtStart || undefined,
          joinedAtEnd: filters.joinedAtEnd || undefined,
          projectCodes: filters.projectCodes?.length ? filters.projectCodes : undefined,
          commercialOwnerIds: filters.commercialOwnerIds?.length ? filters.commercialOwnerIds : undefined,
          isInactive: filters.isInactive === undefined ? false : filters.isInactive,
          ...(filters.declinedRescue === true ? { declinedRescue: true } : {}),
        },
      }),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      handleServiceError(
        new Error(err?.error?.message || err?.error || 'Erro ao atualizar status comercial'),
        'atualizar status comercial',
      );
    }

    const body = await response.json() as {
      success?: boolean;
      data?: {
        statuses?: Array<{
          studentId?: number;
          student_id?: number;
          hasPaid?: boolean;
          has_paid?: boolean;
          paidAt?: string | null;
          paid_at?: string | null;
          hasPhotos?: boolean;
          has_photos?: boolean;
          hasParq?: boolean;
          has_parq?: boolean;
          hasAnamnesis?: boolean;
          has_anamnesis?: boolean;
          hasParqYesAnswers?: boolean;
          has_parq_yes_answers?: boolean;
          parqYesAnswers?: unknown;
          parq_yes_answers?: unknown;
          classification?: StudentClassification | null;
          selectedPaymentMethod?: string | null;
          selected_payment_method?: string | null;
          studentSubscriptionId?: number | null;
          student_subscription_id?: number | null;
          commercialStage?: string | null;
          commercial_stage?: string | null;
          commercialOwnerPortalUserId?: number | null;
          commercial_owner_portal_user_id?: number | null;
          commercialOwnerName?: string | null;
          commercial_owner_name?: string | null;
        }>;
        hasNewLeads?: boolean;
        has_new_leads?: boolean;
      };
      error?: { message?: string };
    };

    if (body.success === false || !body.data) {
      handleServiceError(
        new Error(body.error?.message || 'Erro ao atualizar status comercial'),
        'atualizar status comercial',
      );
    }

    const statuses = (body.data.statuses ?? []).reduce<Record<number, CheckoutLeadOnboardingStatus>>(
      (lookup, status) => {
        const studentId = Number(status.studentId ?? status.student_id);
        if (!Number.isInteger(studentId) || studentId <= 0) {
          return lookup;
        }

        const rawPaymentMethod = status.selectedPaymentMethod ?? status.selected_payment_method;
        const selectedPaymentMethod = rawPaymentMethod === 'pix'
          || rawPaymentMethod === 'boleto'
          || rawPaymentMethod === 'credit_card'
          ? rawPaymentMethod
          : null;
        const rawSubscriptionId = Number(status.studentSubscriptionId ?? status.student_subscription_id);
        const studentSubscriptionId = Number.isInteger(rawSubscriptionId) && rawSubscriptionId > 0
          ? rawSubscriptionId
          : null;
        const rawCommercialStage = status.commercialStage ?? status.commercial_stage;
        const commercialStage = typeof rawCommercialStage === 'string' && rawCommercialStage.trim()
          ? rawCommercialStage.trim()
          : null;
        const ownerIdRaw = Number(
          status.commercialOwnerPortalUserId ?? status.commercial_owner_portal_user_id,
        );
        const ownerNameRaw = status.commercialOwnerName ?? status.commercial_owner_name;

        lookup[studentId] = {
          has_paid: Boolean(status.hasPaid ?? status.has_paid),
          paid_at: (status.paidAt ?? status.paid_at) ?? null,
          has_photos: Boolean(status.hasPhotos ?? status.has_photos),
          has_parq: Boolean(status.hasParq ?? status.has_parq),
          has_anamnesis: Boolean(status.hasAnamnesis ?? status.has_anamnesis),
          parq_has_yes_answers: mapParqYesAnswersFromJson(
            status.parqYesAnswers ?? status.parq_yes_answers,
          ).has_yes_answers || Boolean(status.hasParqYesAnswers ?? status.has_parq_yes_answers),
          parq_yes_answers: mapParqYesAnswersFromJson(
            status.parqYesAnswers ?? status.parq_yes_answers ?? {
              has_yes_answers: status.hasParqYesAnswers ?? status.has_parq_yes_answers,
              answers: [],
            },
          ),
          classification: mapStudentClassificationFromJson(status.classification),
          selected_payment_method: selectedPaymentMethod,
          student_subscription_id: studentSubscriptionId,
          commercial_stage: commercialStage,
          commercial_owner_portal_user_id: Number.isInteger(ownerIdRaw) && ownerIdRaw > 0
            ? ownerIdRaw
            : null,
          commercial_owner_name: typeof ownerNameRaw === 'string' && ownerNameRaw.trim()
            ? ownerNameRaw.trim()
            : null,
        };

        return lookup;
      },
      {},
    );

    return {
      statuses,
      hasNewLeads: Boolean(body.data.hasNewLeads ?? body.data.has_new_leads),
    };
  }

  async updateLeadCancellationReason(params: {
    studentId?: number | null;
    qualificationId?: number | null;
    cancellationReason: string;
  }): Promise<{
    studentId: number | null;
    qualificationId: number | null;
    cancellationReason: string;
  }> {
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData?.session?.access_token;

    const studentId = params.studentId != null
      && Number.isInteger(params.studentId)
      && params.studentId > 0
      ? params.studentId
      : null;
    const qualificationId = params.qualificationId != null
      && Number.isInteger(params.qualificationId)
      && params.qualificationId > 0
      ? params.qualificationId
      : null;

    const response = await fetch(`${config.supabaseFunctionsUrl}/students`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        apikey: config.supabaseAnonKey,
      },
      body: JSON.stringify({
        action: 'update-lead-cancellation-reason',
        payload: {
          ...(studentId != null ? { studentId } : {}),
          ...(qualificationId != null ? { qualificationId } : {}),
          cancellationReason: params.cancellationReason,
        },
      }),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      handleServiceError(
        new Error(err?.error?.message || err?.error || 'Erro ao registrar motivo da desistência'),
        'registrar motivo da desistência',
      );
    }

    const body = await response.json() as {
      success?: boolean;
      data?: {
        studentId?: number | null;
        student_id?: number | null;
        qualificationId?: number | null;
        qualification_id?: number | null;
        cancellationReason?: string;
        cancellation_reason?: string;
      };
      error?: { message?: string };
    };

    if (body.success === false || !body.data) {
      handleServiceError(
        new Error(body.error?.message || 'Erro ao registrar motivo da desistência'),
        'registrar motivo da desistência',
      );
    }

    const responseStudentId = Number(body.data.studentId ?? body.data.student_id);
    const responseQualificationId = Number(
      body.data.qualificationId ?? body.data.qualification_id,
    );

    return {
      studentId: Number.isInteger(responseStudentId) && responseStudentId > 0
        ? responseStudentId
        : studentId,
      qualificationId: Number.isInteger(responseQualificationId) && responseQualificationId > 0
        ? responseQualificationId
        : qualificationId,
      cancellationReason: String(
        body.data.cancellationReason ?? body.data.cancellation_reason ?? params.cancellationReason,
      ),
    };
  }

  async updateCommercialStage(params: {
    studentId?: number | null;
    qualificationId?: number | null;
    commercialStage: string | null;
  }): Promise<{
    studentId: number | null;
    qualificationId: number | null;
    commercialStage: string | null;
  }> {
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData?.session?.access_token;

    const studentId = params.studentId != null
      && Number.isInteger(params.studentId)
      && params.studentId > 0
      ? params.studentId
      : null;
    const qualificationId = params.qualificationId != null
      && Number.isInteger(params.qualificationId)
      && params.qualificationId > 0
      ? params.qualificationId
      : null;

    const response = await fetch(`${config.supabaseFunctionsUrl}/students`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        apikey: config.supabaseAnonKey,
      },
      body: JSON.stringify({
        action: 'update-commercial-stage',
        payload: {
          ...(studentId != null ? { studentId } : {}),
          ...(qualificationId != null ? { qualificationId } : {}),
          commercialStage: params.commercialStage,
        },
      }),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      handleServiceError(
        new Error(err?.error?.message || err?.error || 'Erro ao atualizar estágio comercial'),
        'atualizar estágio comercial',
      );
    }

    const body = await response.json() as {
      success?: boolean;
      data?: {
        studentId?: number | null;
        student_id?: number | null;
        qualificationId?: number | null;
        qualification_id?: number | null;
        commercialStage?: string | null;
        commercial_stage?: string | null;
      };
      error?: { message?: string };
    };

    if (body.success === false || !body.data) {
      handleServiceError(
        new Error(body.error?.message || 'Erro ao atualizar estágio comercial'),
        'atualizar estágio comercial',
      );
    }

    const nextStage = body.data.commercialStage ?? body.data.commercial_stage ?? params.commercialStage;
    const responseStudentId = Number(body.data.studentId ?? body.data.student_id);
    const responseQualificationId = Number(
      body.data.qualificationId ?? body.data.qualification_id,
    );

    return {
      studentId: Number.isInteger(responseStudentId) && responseStudentId > 0
        ? responseStudentId
        : studentId,
      qualificationId: Number.isInteger(responseQualificationId) && responseQualificationId > 0
        ? responseQualificationId
        : qualificationId,
      commercialStage: typeof nextStage === 'string' && nextStage ? nextStage : null,
    };
  }

  async rescueDeclinedLead(params: {
    studentId?: number | null;
    qualificationId?: number | null;
  }): Promise<{
    studentId: number | null;
    qualificationId: number | null;
    commercialStage: string | null;
  }> {
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData?.session?.access_token;

    const studentId = params.studentId != null
      && Number.isInteger(params.studentId)
      && params.studentId > 0
      ? params.studentId
      : null;
    const qualificationId = params.qualificationId != null
      && Number.isInteger(params.qualificationId)
      && params.qualificationId > 0
      ? params.qualificationId
      : null;

    const response = await fetch(`${config.supabaseFunctionsUrl}/students`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        apikey: config.supabaseAnonKey,
      },
      body: JSON.stringify({
        action: 'rescue-declined-lead',
        payload: {
          ...(studentId != null ? { studentId } : {}),
          ...(qualificationId != null ? { qualificationId } : {}),
        },
      }),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      handleServiceError(
        new Error(err?.error?.message || err?.error || 'Erro ao resgatar aluna declinada'),
        'resgatar aluna declinada',
      );
    }

    const body = await response.json() as {
      success?: boolean;
      data?: {
        studentId?: number | null;
        student_id?: number | null;
        qualificationId?: number | null;
        qualification_id?: number | null;
        commercialStage?: string | null;
        commercial_stage?: string | null;
      };
      error?: { message?: string };
    };

    if (body.success === false || !body.data) {
      handleServiceError(
        new Error(body.error?.message || 'Erro ao resgatar aluna declinada'),
        'resgatar aluna declinada',
      );
    }

    const nextStage = body.data.commercialStage ?? body.data.commercial_stage ?? null;
    const responseStudentId = Number(body.data.studentId ?? body.data.student_id);
    const responseQualificationId = Number(
      body.data.qualificationId ?? body.data.qualification_id,
    );

    return {
      studentId: Number.isInteger(responseStudentId) && responseStudentId > 0
        ? responseStudentId
        : studentId,
      qualificationId: Number.isInteger(responseQualificationId) && responseQualificationId > 0
        ? responseQualificationId
        : qualificationId,
      commercialStage: typeof nextStage === 'string' && nextStage ? nextStage : null,
    };
  }

  async listCommercialOwners(): Promise<{
    currentPortalUserId: number | null;
    users: Array<{ id: number; portal_user_id: number; label: string }>;
  }> {
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData?.session?.access_token;

    const response = await fetch(`${config.supabaseFunctionsUrl}/students`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        apikey: config.supabaseAnonKey,
      },
      body: JSON.stringify({
        action: 'list-commercial-owners',
        payload: {},
      }),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      handleServiceError(
        new Error(err?.error?.message || err?.error || 'Erro ao carregar responsáveis comerciais'),
        'carregar responsáveis comerciais',
      );
    }

    const body = await response.json() as {
      success?: boolean;
      data?: {
        currentPortalUserId?: number | null;
        current_portal_user_id?: number | null;
        users?: Array<{ id?: number; portal_user_id?: number; label?: string }>;
      };
      error?: { message?: string };
    };

    if (body.success === false || !body.data) {
      handleServiceError(
        new Error(body.error?.message || 'Erro ao carregar responsáveis comerciais'),
        'carregar responsáveis comerciais',
      );
    }

    const currentRaw = Number(body.data.currentPortalUserId ?? body.data.current_portal_user_id);
    const users = (body.data.users ?? [])
      .map((user) => {
        const id = Number(user.id ?? user.portal_user_id);
        const label = typeof user.label === 'string' ? user.label.trim() : '';
        if (!Number.isInteger(id) || id <= 0 || !label) {
          return null;
        }
        return { id, portal_user_id: id, label };
      })
      .filter((user): user is { id: number; portal_user_id: number; label: string } => user !== null);

    return {
      currentPortalUserId: Number.isInteger(currentRaw) && currentRaw > 0 ? currentRaw : null,
      users,
    };
  }

  async assignCommercialOwner(params: {
    qualificationId: number;
    ownerPortalUserId?: number | null;
    assignToSelf?: boolean;
  }): Promise<{
    qualificationId: number;
    commercialOwnerPortalUserId: number | null;
    commercialOwnerName: string | null;
  }> {
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData?.session?.access_token;

    const response = await fetch(`${config.supabaseFunctionsUrl}/students`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        apikey: config.supabaseAnonKey,
      },
      body: JSON.stringify({
        action: 'assign-commercial-owner',
        payload: {
          qualificationId: params.qualificationId,
          ...(params.assignToSelf ? { assignToSelf: true } : { ownerPortalUserId: params.ownerPortalUserId ?? null }),
        },
      }),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      handleServiceError(
        new Error(err?.error?.message || err?.error || 'Erro ao atribuir responsável comercial'),
        'atribuir responsável comercial',
      );
    }

    const body = await response.json() as {
      success?: boolean;
      data?: {
        qualificationId?: number;
        qualification_id?: number;
        commercialOwnerPortalUserId?: number | null;
        commercial_owner_portal_user_id?: number | null;
        commercialOwnerName?: string | null;
        commercial_owner_name?: string | null;
      };
      error?: { message?: string };
    };

    if (body.success === false || !body.data) {
      handleServiceError(
        new Error(body.error?.message || 'Erro ao atribuir responsável comercial'),
        'atribuir responsável comercial',
      );
    }

    const qualificationId = Number(body.data.qualificationId ?? body.data.qualification_id);
    const ownerId = Number(
      body.data.commercialOwnerPortalUserId ?? body.data.commercial_owner_portal_user_id,
    );
    const ownerNameRaw = body.data.commercialOwnerName ?? body.data.commercial_owner_name;

    return {
      qualificationId: Number.isInteger(qualificationId) && qualificationId > 0
        ? qualificationId
        : params.qualificationId,
      commercialOwnerPortalUserId: Number.isInteger(ownerId) && ownerId > 0 ? ownerId : null,
      commercialOwnerName: typeof ownerNameRaw === 'string' && ownerNameRaw.trim()
        ? ownerNameRaw.trim()
        : null,
    };
  }

  async ensureLeadStudent(qualificationId: number): Promise<{
    studentId: number;
    qualificationId: number;
    created: boolean;
  }> {
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData?.session?.access_token;

    const response = await fetch(`${config.supabaseFunctionsUrl}/students`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        apikey: config.supabaseAnonKey,
      },
      body: JSON.stringify({
        action: 'ensure-lead-student',
        payload: {
          qualificationId,
        },
      }),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      handleServiceError(
        new Error(err?.error?.message || err?.error || 'Erro ao criar aluna do lead'),
        'criar aluna do lead',
      );
    }

    const body = await response.json() as {
      success?: boolean;
      data?: {
        studentId?: number;
        student_id?: number;
        qualificationId?: number;
        qualification_id?: number;
        created?: boolean;
      };
      error?: { message?: string };
    };

    if (body.success === false || !body.data) {
      handleServiceError(
        new Error(body.error?.message || 'Erro ao criar aluna do lead'),
        'criar aluna do lead',
      );
    }

    const studentId = Number(body.data.studentId ?? body.data.student_id);
    const responseQualificationId = Number(
      body.data.qualificationId ?? body.data.qualification_id ?? qualificationId,
    );

    if (!Number.isInteger(studentId) || studentId <= 0) {
      handleServiceError(
        new Error('Resposta inválida: studentId ausente.'),
        'criar aluna do lead',
      );
    }

    return {
      studentId,
      qualificationId: Number.isInteger(responseQualificationId) && responseQualificationId > 0
        ? responseQualificationId
        : qualificationId,
      created: Boolean(body.data.created),
    };
  }
}

export const LeadService = new LeadServiceClass();
